import * as echarts from 'echarts';
import moment from 'moment';
import comOpt from './common-options.json';
import { merge } from 'lodash';
import utils from "@/utils";

// Helper function to format labels
const formatLabels = (data, field, period = 'daily') => {
  if (field === 'hour_activity') {
    return data.map(day => ({
      ...day,
      label: `${day.label}:00`,
    }));
  }
  
  if (field !== 'growth') return data;
  
  const formatted = data.map(day => {
    let dateFormat = 'MMM DD';
    
    // Use different date formats based on period
    if (period === 'weekly') {
      dateFormat = 'MMM DD';  // Will show start of week
    } else if (period === 'monthly') {
      dateFormat = 'MMM YYYY';
    }
    
    return {
      ...day,
      unix: day.date,
      date: moment(day.date).format(dateFormat),
    };
  });
  
  return formatted.sort((a, b) => a.unix - b.unix);
};

// Helper function to adjust color brightness
const adjustColorBrightness = (col, amt) => {
  let usePound = false;
  if (col[0] === '#') {
    col = col.slice(1);
    usePound = true;
  }

  const num = parseInt(col, 16);
  let r = (num >> 16) + amt;
  let b = ((num >> 8) & 0x00ff) + amt;
  let g = (num & 0x0000ff) + amt;

  r = Math.min(255, Math.max(0, r));
  b = Math.min(255, Math.max(0, b));
  g = Math.min(255, Math.max(0, g));

  return (
    (usePound ? '#' : '') +
    (g | (b << 8) | (r << 16)).toString(16).padStart(6, '0')
  );
};

const removeDuplicates = (data, key) => {
  const seen = new Set();
  return data.filter(item => {
    const identifier = key ? item[key] : JSON.stringify(item);
    return seen.has(identifier) ? false : seen.add(identifier);
  });
};

const create = ({ pages, ref, chartData, field, category, colors, period = 'daily' }) => {
  return new Promise((resolve, reject) => {
    chartData = Array.isArray(chartData) ? chartData : [];

    // Remove duplicates in chart data based on a unique key (e.g., `date` or `category`)
    const deduplicatedData = removeDuplicates(chartData, category);

    const chartDom = ref[0];
    if (!chartDom) {
      return reject();
    }

    const myChart = echarts.init(chartDom);

    // Format and process the deduplicated data
    const data = formatLabels(deduplicatedData, field, period);
    
    // For xAxis data, determine the correct field to use
    let xAxisData = [];
    
    if (Array.isArray(data) && data.length > 0) {
      if (category && data[0] && data[0][category] !== undefined) {
        // Use the specified category if it exists
        xAxisData = data.map(item => item[category]);
      } else if (data[0] && data[0].date !== undefined) {
        // Fall back to date field
        xAxisData = data.map(item => item.date);
      } else {
        // Last resort: use an index
        xAxisData = data.map((_, i) => `Item ${i+1}`);
      }
    }

    const series = pages.map((page, index) => {
      const dataValues = data.map(item => item[`${field}_${index}`] || 0);
      return {
        name: page.owner_name,
        type: 'bar',
        stack: null,
        data: dataValues,
        barMaxWidth: 20,
        z: 220,
        barGap: '50%',
        itemStyle: {
          color: function (params) {
            return params.value < 0
              ? adjustColorBrightness(page.colorCode, -20)
              : page.colorCode;
          },
          borderRadius: [3, 3, 3, 3],
        },
        emphasis: {
          focus: 'series',
          itemStyle: {
            color: page.colorCode,
          },
        },
      };
    });

    const option = merge({}, comOpt, {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          label: { show: false },
          z: 200,
        },
        formatter: params => {
          if (params.length === 0) return '';
          let tooltipText = `<div class='tooltip'><span class='date'>${params[0].axisValueLabel}</span><ul class='tooltip-list'>`;
          let hasData = false;
          params.forEach(param => {
            if (param.value != null && param.value !== 0) {
              hasData = true;
              const page = pages.find(p => p.owner_name === param.seriesName);
              tooltipText += `
                <li data-value="${param.value}">
                  <div class="soc-sea ${page.colorName}">
                    <span class="soc-ico soc-${page.social.toLowerCase()}"></span>
                    ${page.owner_name_medium}
                  </div>${param.value}${field !== 'growth' && field !== 'hashtag_cnt' ? '%' : ''}
                </li>`;
            }
          });
          tooltipText += '</ul></div>';
          return hasData ? tooltipText : '';
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
      },
      yAxis: {
        type: 'value',
      },
      series: series,
    });

    myChart.setOption(option);

    window.addEventListener('resize', myChart.resize);
    resolve({ chart: myChart });
  });
};


export async function exportChart({
  chart,
  container,
  chartData,
  pages,
  field,
  period,
  colors,
  category,
  type = 'svg'
}) {
  if (!chart) {
    return;
  }

  const filename = `columns_chart_${type === 'csv' ? 'data_' : ''}${new Date().toISOString().split('T')[0]}`;

  if (type === 'svg') {
    if (!container) {
      return;
    }

    try {
      if (chart && chart.dispose) {
        chart.dispose();
      }
      
      echarts.dispose(container);
      const svgChart = echarts.init(container, null, { renderer: 'svg' });
      const deduplicatedData = removeDuplicates(chartData, category || 'date');
      const data = formatLabels(deduplicatedData, field, period);
      
      let xAxisData = [];
      
      if (Array.isArray(data) && data.length > 0) {
        if (category && data[0] && data[0][category] !== undefined) {
          xAxisData = data.map(item => item[category]);
        } else if (data[0] && data[0].date !== undefined) {
          xAxisData = data.map(item => item.date);
        } else {
          xAxisData = data.map((_, i) => `Item ${i+1}`);
        }
      }
      
      const series = pages.map((page, index) => {
        const dataValues = data.map(item => item[`${field}_${index}`] || 0);
        return {
          name: page.owner_name,
          type: 'bar',
          stack: null,
          data: dataValues,
          barMaxWidth: 20,
          z: 220,
          barGap: '50%',
          itemStyle: {
            color: function (params) {
              return params.value < 0
                ? adjustColorBrightness(page.colorCode, -20)
                : page.colorCode;
            },
            borderRadius: [3, 3, 3, 3],
          }
        };
      });
      
      const option = merge({}, comOpt, {
        backgroundColor: 'transparent',
        xAxis: {
          type: 'category',
          data: xAxisData,
        },
        yAxis: {
          type: 'value',
        },
        series: series,
        animation: false
      });
      
      svgChart.setOption(option);
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const svgElement = container.querySelector('svg');
      if (!svgElement) {
        throw new Error("SVG element not found in container");
      }
      
      const svgContent = svgElement.outerHTML;
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      
      utils.downloadFile(blob, `${filename}.svg`);
      if (svgChart) {
        svgChart.dispose();
      }
      
      const newChart = await create({
        pages,
        ref: [container],
        chartData,
        field,
        period,
        category: category || "date",
        colors,
        renderer: 'canvas',
        animation: true
      });
      
      return newChart;
    } catch (error) {
      try {
        return await create({
          pages,
          ref: [container],
          chartData,
          field,
          period,
          category: category || "date",
          colors,
          renderer: 'canvas',
          animation: true
        });
      } catch (revertError) {
      }
    }
  } else if (type === 'csv') {
    if (!chartData || chartData.length === 0) {
      return;
    }
    
    const categories = chartData.map(item => {
      const date = moment(item.date);
      if (period === 'weekly') {
        const weekNum = date.week();
        const year = date.year();
        return `Week ${weekNum}, ${year}`;
      } else if (period === 'monthly') {
        return date.format('MMM YYYY');
      }
      return date.format('MMM DD, YYYY');
    });
    
    const series = [];
    const pageCount = pages.length;
    
    for (let i = 0; i < pageCount; i++) {
      const page = pages[i];
      const fieldName = `${field}_${i}`;
      
      if (chartData.some(item => item[fieldName] !== undefined)) {
        series.push({
          name: page.owner_name_medium || `Page ${i+1}`,
          data: chartData.map(item => item[fieldName] || 0)
        });
      }
    }
    
    if (series.length === 0 && chartData.some(item => item[field] !== undefined)) {
      series.push({
        name: field,
        data: chartData.map(item => item[field] || 0)
      });
    }
    
    utils.exportDataToCSV({
      series,
      categories,
      seriesNames: series.map(s => s.name),
      filename
    });
  }
}

export default create;
